import { Scene } from '@babylonjs/core';

export interface KeyBindings {
  moveLeft: string[];
  moveRight: string[];
  moveUp: string[];
  moveDown: string[];
  jump: string[];
  action: string[];
  action2: string[];
  action3: string[];
}

export interface VirtualGamepadState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  attack: boolean;
  jump: boolean;
  cycleParty: boolean;
}

export class InputManager {
  private keys: { [key: string]: boolean } = {};
  private movementEnabled = true;
  private virtualEnabled = false;
  private virtualState: VirtualGamepadState = {
    left: false,
    right: false,
    up: false,
    down: false,
    attack: false,
    jump: false,
    cycleParty: false,
  };
  private bindings: KeyBindings = {
    moveLeft: ['a', 'arrowleft'],
    moveRight: ['d', 'arrowright'],
    moveUp: ['w', 'arrowup'],
    moveDown: ['s', 'arrowdown'],
    jump: [' '],
    action: ['e', 'enter'],
    action2: ['q', 'shift'],
    action3: ['r']
  };
  
  constructor(_scene: Scene, customBindings?: Partial<KeyBindings>) {
    if (customBindings) {
      this.bindings = { ...this.bindings, ...customBindings };
    }

    // BYPASS: Use window events directly instead of BabylonJS observable
    // BabylonJS keyboard observable doesn't work with Playwright automated testing
    
    window.addEventListener('keydown', (e) => {
      const key = e.key.toLowerCase();
      this.keys[key] = true;
    });
    
    window.addEventListener('keyup', (e) => {
      const key = e.key.toLowerCase();
      this.keys[key] = false;
    });
  }

  isPressed(key: string): boolean {
    return this.keys[key.toLowerCase()] || false;
  }

  isActionPressed(action: keyof KeyBindings): boolean {
    return this.bindings[action].some(key => this.isPressed(key));
  }

  // Action mappings
  get moveLeft(): boolean {
    if (!this.movementEnabled) return false;
    return this.isActionPressed('moveLeft') || (this.virtualEnabled && this.virtualState.left);
  }

  get moveRight(): boolean {
    if (!this.movementEnabled) return false;
    return this.isActionPressed('moveRight') || (this.virtualEnabled && this.virtualState.right);
  }

  get jump(): boolean {
    if (!this.movementEnabled) return false;
    // D-pad up is mapped to jump on mobile.
    const virtualJump = this.virtualEnabled && this.virtualState.jump;
    return this.isActionPressed('jump') || virtualJump;
  }

  /** Vertical input for side-scroller mode: -1 (down), 0, 1 (up) */
  get moveUp(): boolean {
    if (!this.movementEnabled) return false;
    return this.isActionPressed('moveUp') || (this.virtualEnabled && this.virtualState.up);
  }

  get moveDown(): boolean {
    if (!this.movementEnabled) return false;
    return this.isActionPressed('moveDown') || (this.virtualEnabled && this.virtualState.down);
  }

  /** Get vertical input as -1, 0, or 1 for side-scroller mode */
  getVerticalInput(): number {
    if (!this.movementEnabled) return 0;
    const up = this.moveUp ? 1 : 0;
    const down = this.moveDown ? 1 : 0;
    return up - down;
  }

  get action(): boolean {
    return this.isActionPressed('action') || (this.virtualEnabled && this.virtualState.attack);
  }

  get action2(): boolean {
    return this.isActionPressed('action2');
  }

  get action3(): boolean {
    return this.isActionPressed('action3');
  }

  /** Enable/disable the virtual gamepad as an input source. */
  setVirtualGamepadEnabled(enabled: boolean): void {
    this.virtualEnabled = enabled;
    if (!enabled) {
      this.clearVirtualGamepadState();
    }
  }

  /** Replace the current virtual gamepad state (typically from the web component). */
  setVirtualGamepadState(next: Partial<VirtualGamepadState>): void {
    this.virtualState = {
      ...this.virtualState,
      ...next,
    };
  }

  clearVirtualGamepadState(): void {
    this.virtualState = {
      left: false,
      right: false,
      up: false,
      down: false,
      attack: false,
      jump: false,
      cycleParty: false,
    };
  }

  /** True while the party-cycle button is held on the virtual gamepad OR 'c' key is pressed. */
  get cycleParty(): boolean {
    return (this.virtualEnabled && this.virtualState.cycleParty) || this.isPressed('c');
  }

  /**
   * Enable/disable character movement-related actions (left/right/jump).
   * Useful when another system (e.g. free-roam camera) needs WASD.
   */
  setMovementEnabled(enabled: boolean): void {
    this.movementEnabled = enabled;
    if (!enabled) {
      // Avoid "stuck" input when toggling modes.
      const toClear = new Set<string>([
        ...this.bindings.moveLeft,
        ...this.bindings.moveRight,
        ...this.bindings.jump,
      ].map((k) => k.toLowerCase()));

      for (const k of toClear) {
        this.keys[k] = false;
      }

      // Also clear movement-related virtual controls.
      this.virtualState.left = false;
      this.virtualState.right = false;
      this.virtualState.up = false;
      this.virtualState.down = false;
      this.virtualState.jump = false;
    }
  }

  setBindings(newBindings: Partial<KeyBindings>): void {
    this.bindings = { ...this.bindings, ...newBindings };
  }

  getBindings(): KeyBindings {
    return { ...this.bindings };
  }
}
